import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getIdTokenWhenReady,
  ROLE_CONTEXT_CHANGED_EVENT,
  setHasProviderMembership,
  withSchoolName,
} from '../../Components/RoleSwitcher/roleContext';
import { switchContextAndVerify } from '../../api/memberships/switchContext';
import { logError } from '../../rollbar';

const NOVA_URL = import.meta.env.VITE_WS_MARKETPLACE_APP_URL || '';

interface Membership {
  id: string;
  identity_id: string;
  context_type: string;
  context_id: string;
  role: string;
  org_name?: string | null;
}

interface MembershipsResponse {
  memberships: Membership[];
  active_membership_id: string | null;
}

const ROLE_LABEL_KEYS: Record<string, string> = {
  director: 'roles.page.role.director',
  location_admin: 'roles.page.role.locationAdmin',
  teacher: 'roles.page.role.teacher',
  support_staff: 'roles.page.role.supportStaff',
  parent: 'roles.page.role.parent',
  guardian: 'roles.page.role.guardian',
  emergency_contact: 'roles.page.role.emergencyContact',
};

const CONTEXT_LABEL_KEYS: Record<string, string> = {
  provider: 'roles.page.context.childcareProvider',
  family: 'roles.page.context.family',
  sponsor: 'roles.page.context.sponsor',
  platform: 'roles.page.context.platform',
};

export function MyRolesPage() {
  const { t } = useTranslation();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [activeMembershipId, setActiveMembershipId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchMemberships = useCallback(async () => {
    try {
      const token = await getIdTokenWhenReady();
      if (!token) return;

      const res = await fetch(`${NOVA_URL}/firebase-api/memberships`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data: MembershipsResponse = await res.json();
        setMemberships(data.memberships);
        setActiveMembershipId(data.active_membership_id);
        // This page changes the answer (removing your last provider role), so it has to keep the
        // marker RestrictedRoute reads honest — a stale '1' holds the next guarded route on a
        // transition that can never finish.
        setHasProviderMembership(data.memberships.some((m) => m.context_type !== 'family'));
        // A refetch that works clears the earlier failure, rather than parking a red message
        // on the page for the rest of the session.
        setError(null);
      } else if (res.status !== 404) {
        // 404 means the flag is off — nothing to show. Anything else is a
        // real failure; an empty roles list here reads as "you have no
        // roles", which is worse than an error.
        logError('MyRoles: memberships load failed', null, { status: res.status });
        setError(t('roles.page.loadError'));
      }
    } catch (err) {
      logError('MyRoles: memberships load failed', err instanceof Error ? err : null, {});
      setError(t('roles.page.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Firebase restores the session asynchronously, so getAuth().currentUser can
  // still be null when this page mounts on a fresh load. A plain mount fetch
  // would bail on the missing token and strand the list empty forever, so wait
  // for auth to settle before fetching. If it settles signed-out, show the load
  // error — an empty list reads as "you have no roles".
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      if (user) {
        fetchMemberships();
      } else {
        setError(t('roles.page.loadError'));
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [fetchMemberships, t]);

  // The header RoleSwitcher can auto-align the active context (family -> provider)
  // right after this page fetched, leaving the pill and this list disagreeing for
  // as long as the page sits open (MP-6831). Refetch when it announces the change.
  useEffect(() => {
    const onRoleContextChanged = () => {
      fetchMemberships();
    };
    window.addEventListener(ROLE_CONTEXT_CHANGED_EVENT, onRoleContextChanged);
    return () => window.removeEventListener(ROLE_CONTEXT_CHANGED_EVENT, onRoleContextChanged);
  }, [fetchMemberships]);

  const handleAddParent = useCallback(async () => {
    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getIdTokenWhenReady();
      if (!token) {
        logError('MyRoles: no signed-in user for add role', null, {});
        setError(t('roles.page.addError'));
        return;
      }

      const res = await fetch(`${NOVA_URL}/firebase-api/memberships`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: 'parent' }),
      });

      if (res.ok) {
        setSuccess(t('roles.page.parentAddedSuccess'));
        setShowAddPanel(false);
        await fetchMemberships();
      } else {
        const data = await res.json();
        if (data.error === 'already_exists') {
          setSuccess(t('roles.page.parentExistsMessage'));
          setShowAddPanel(false);
        } else {
          setError(data.error || t('roles.page.addError'));
        }
      }
    } catch {
      setError(t('roles.page.addError'));
    } finally {
      setAdding(false);
    }
  }, [fetchMemberships, t]);

  const handleRemove = useCallback(
    async (membershipId: string) => {
      setRemoving(membershipId);
      setError(null);
      setSuccess(null);

      try {
        const token = await getIdTokenWhenReady();
        if (!token) {
          logError('MyRoles: no signed-in user for remove role', null, { membershipId });
          setError(t('roles.page.removeError'));
          return;
        }

        const res = await fetch(`${NOVA_URL}/firebase-api/memberships/${membershipId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const wasActive = membershipId === activeMembershipId;
          // Refresh the list first: it is what updates the provider-membership marker.
          await fetchMemberships();

          if (wasActive) {
            // CCMS reads role claims once at boot, so after removing the role it is running as,
            // only a fresh token plus a reload makes the app and the token agree. Without this the
            // shell kept rendering the deleted role's claims while the pill fell back to "Select
            // role".
            await getAuth().currentUser?.getIdToken(true);
            window.location.reload();
            return;
          }

          setSuccess(t('roles.page.removedSuccess'));
        } else {
          const data = await res.json();
          if (data.error === 'last_membership') {
            setError(t('roles.page.onlyRoleError'));
          } else {
            setError(data.error || t('roles.page.removeError'));
          }
        }
      } catch {
        setError(t('roles.page.removeError'));
      } finally {
        setRemoving(null);
      }
    },
    [activeMembershipId, fetchMemberships, t]
  );

  const handleSwitch = useCallback(
    async (membershipId: string) => {
      setSwitching(membershipId);
      setError(null);
      setSuccess(null);

      try {
        const result = await switchContextAndVerify(membershipId);

        if (result.status === 'ok' || result.status === 'unavailable') {
          if (result.active?.context_type === 'family') {
            window.location.href = `${NOVA_URL}/parents`;
          } else {
            window.location.reload();
          }
          return;
        }

        if (result.status === 'superseded') {
          window.location.reload();
          return;
        }

        if (result.status === 'signed_out') {
          logError('MyRoles: no signed-in user for switch', null, { membershipId });
          setError(t('roles.page.sessionEndedError'));
        } else if (result.status === 'not_ready') {
          logError('MyRoles: claims not updated after refresh', null, { membershipId, ...result });
          setError(t('roles.page.roleNotReadyError'));
        } else {
          setError(result.error || t('roles.page.switchError'));
        }
        setSwitching(null);
      } catch {
        setError(t('roles.page.switchError'));
        setSwitching(null);
      }
    },
    [t]
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-gray-500">{t('roles.page.loadingLabel')}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-8">
      <h1 className="mb-6 text-2xl font-bold">{t('roles.page.heading')}</h1>

      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">{success}</div>
      )}

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">{error}</div>}

      <div className="space-y-4">
        {memberships.map((membership) => {
          const isActive = membership.id === activeMembershipId;
          return (
            <div
              key={membership.id}
              className={`rounded-lg border p-4 ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">
                      {withSchoolName(
                        membership.org_name,
                        ROLE_LABEL_KEYS[membership.role] ? t(ROLE_LABEL_KEYS[membership.role]) : membership.role
                      )}
                    </span>
                    {isActive && (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {t('roles.page.activeStatus')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {CONTEXT_LABEL_KEYS[membership.context_type]
                      ? t(CONTEXT_LABEL_KEYS[membership.context_type])
                      : membership.context_type}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {!isActive && memberships.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleSwitch(membership.id)}
                      disabled={switching !== null || removing !== null}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {switching === membership.id ? t('roles.page.switchingLabel') : t('roles.page.switchAction')}
                    </button>
                  )}
                  {memberships.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemove(membership.id)}
                      disabled={switching !== null || removing !== null}
                      className="text-sm text-gray-400 hover:text-red-600 disabled:opacity-50"
                    >
                      {removing === membership.id ? t('roles.page.removingLabel') : t('roles.page.removeAction')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        {showAddPanel ? (
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="mb-4 font-semibold">{t('roles.page.addRoleHeading')}</h3>

            <div className="space-y-3">
              <button
                type="button"
                onClick={handleAddParent}
                disabled={adding}
                className="w-full rounded-lg border border-gray-200 p-3 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                <div className="font-medium">
                  {adding ? t('roles.page.addingLabel') : t('roles.page.parentRoleLabel')}
                </div>
                <div className="text-sm text-gray-500">{t('roles.page.parentRoleDescription')}</div>
              </button>

              <div className="w-full rounded-lg border border-gray-200 p-3 text-left opacity-50">
                <div className="font-medium">{t('roles.page.providerRoleLabel')}</div>
                <div className="text-sm text-gray-500">{t('roles.page.providerRoleDescription')}</div>
                <div className="mt-1 text-xs text-gray-400">{t('roles.page.comingSoonLabel')}</div>
              </div>

              <div className="w-full rounded-lg border border-gray-200 p-3 text-left opacity-50">
                <div className="font-medium">{t('roles.page.staffRoleLabel')}</div>
                <div className="text-sm text-gray-500">{t('roles.page.staffRoleDescription')}</div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowAddPanel(false)}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700"
            >
              {t('roles.page.cancelAction')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddPanel(true)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            {t('roles.page.addRoleAction')}
          </button>
        )}
      </div>
    </div>
  );
}

export default MyRolesPage;
